import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Edit2, Calendar, User, Tag, Folder, Columns } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import { TaskBoard, TaskBoardTask, TaskCategory, TaskProject, TaskResponsible } from '../types';

type BoardStatus = string;

const DEFAULT_STATUSES: BoardStatus[] = ['Pendiente', 'En curso', 'Hecho'];

const TaskBoardsPage: React.FC = () => {
  const {
    taskBoards,
    taskBoardTasks,
    taskResponsibles,
    taskCategories,
    taskProjects,
    fetchTaskBoards,
    fetchTaskBoardTasks,
    fetchTaskResponsibles,
    fetchTaskCategories,
    fetchTaskProjects,
    addTaskBoard,
    updateTaskBoard,
    deleteTaskBoard,
    addTaskBoardTask,
    updateTaskBoardTask,
    deleteTaskBoardTask,
    addTaskResponsible,
    updateTaskResponsible,
    deleteTaskResponsible,
    addTaskCategory,
    updateTaskCategory,
    deleteTaskCategory,
    addTaskProject,
    updateTaskProject,
    deleteTaskProject,
  } = useAppStore();

  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardDescription, setNewBoardDescription] = useState('');
  const [editingBoard, setEditingBoard] = useState<TaskBoard | null>(null);
  const [editingBoardStatuses, setEditingBoardStatuses] = useState<string>('');
  const [showNewBoardForm, setShowNewBoardForm] = useState(false);

  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskStatus, setNewTaskStatus] = useState<BoardStatus>(DEFAULT_STATUSES[0]);
  const [newTaskDueDate, setNewTaskDueDate] = useState<string>('');
  const [newTaskResponsibleId, setNewTaskResponsibleId] = useState<string>('');
  const [newTaskCategoryId, setNewTaskCategoryId] = useState<string>('');
  const [newTaskProjectId, setNewTaskProjectId] = useState<string>('');

  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskValues, setEditingTaskValues] = useState<Partial<TaskBoardTask>>({});

  useEffect(() => {
    fetchTaskBoards();
    fetchTaskResponsibles();
    fetchTaskCategories();
    fetchTaskProjects();
  }, [fetchTaskBoards, fetchTaskResponsibles, fetchTaskCategories, fetchTaskProjects]);

  useEffect(() => {
    if (!selectedBoardId && taskBoards.length > 0) {
      setSelectedBoardId(taskBoards[0].id);
    }
  }, [taskBoards, selectedBoardId]);

  useEffect(() => {
    if (selectedBoardId) {
      fetchTaskBoardTasks(selectedBoardId);
    }
  }, [selectedBoardId, fetchTaskBoardTasks]);

  const selectedBoard = useMemo(
    () => taskBoards.find(b => b.id === selectedBoardId) || null,
    [taskBoards, selectedBoardId]
  );

  const boardTasks = useMemo(
    () => taskBoardTasks.filter(t => t.board_id === selectedBoardId),
    [taskBoardTasks, selectedBoardId]
  );

  const boardStatuses: BoardStatus[] =
    (selectedBoard?.status_columns && selectedBoard.status_columns.length > 0
      ? selectedBoard.status_columns
      : DEFAULT_STATUSES);

  const groupedTasks = useMemo(() => {
    const groups: Record<BoardStatus, TaskBoardTask[]> = {};
    boardStatuses.forEach(status => {
      groups[status] = [];
    });

    for (const task of boardTasks) {
      const status =
        boardStatuses.find(s => s.toLowerCase() === (task.status || '').toLowerCase()) ||
        boardStatuses[0];
      if (!groups[status]) groups[status] = [];
      groups[status].push(task);
    }

    return groups;
  }, [boardTasks, boardStatuses]);

  const handleCreateBoard = async () => {
    const name = newBoardName.trim();
    if (!name) return;
    try {
      await addTaskBoard({
        id: '',
        name,
        description: newBoardDescription || null,
        status_columns: DEFAULT_STATUSES,
        created_at: '',
        updated_at: '',
        is_archived: false,
      });
      setNewBoardName('');
      setNewBoardDescription('');
    } catch {
      // error centralizado en store
    }
  };

  const handleUpdateBoard = async () => {
    if (!editingBoard) return;
    const name = editingBoard.name.trim();
    if (!name) return;

    const rawStatuses = editingBoardStatuses || '';
    const statuses = rawStatuses
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const finalStatuses =
      statuses.length > 0 ? statuses : DEFAULT_STATUSES;

    try {
      await updateTaskBoard(editingBoard.id, {
        name,
        description: editingBoard.description || null,
        status_columns: finalStatuses,
      });
      setEditingBoard(null);
      setEditingBoardStatuses('');
    } catch {
      // error centralizado en store
    }
  };

  const handleDeleteBoard = async (board: TaskBoard) => {
    const confirmed = window.confirm(
      '¿Seguro que querés eliminar este tablero y todas sus tareas?'
    );
    if (!confirmed) return;
    try {
      await deleteTaskBoard(board.id);
      if (selectedBoardId === board.id) {
        setSelectedBoardId(null);
      }
    } catch {
      // error centralizado en store
    }
  };

  const handleCreateTask = async () => {
    if (!selectedBoard) return;
    const title = newTaskTitle.trim();
    if (!title) return;

    try {
      const nextPosition =
        boardTasks.length > 0
          ? Math.max(...boardTasks.map(t => t.position ?? 0)) + 1
          : 0;

      await addTaskBoardTask({
        id: '',
        board_id: selectedBoard.id,
        title,
        description: null,
        status: newTaskStatus,
        due_date: newTaskDueDate || null,
        responsible_id: newTaskResponsibleId || null,
        category_id: newTaskCategoryId || null,
        project_id: newTaskProjectId || null,
        position: nextPosition,
        created_at: '',
        updated_at: '',
      });

      setNewTaskTitle('');
      setNewTaskDueDate('');
      setNewTaskResponsibleId('');
      setNewTaskCategoryId('');
      setNewTaskProjectId('');
      setNewTaskStatus(DEFAULT_STATUSES[0]);
    } catch {
      // error centralizado en store
    }
  };

  const startEditTask = (task: TaskBoardTask) => {
    setEditingTaskId(task.id);
    setEditingTaskValues(task);
  };

  const handleSaveTask = async () => {
    if (!editingTaskId) return;
    try {
      await updateTaskBoardTask(editingTaskId, {
        title: editingTaskValues.title,
        status: editingTaskValues.status,
        due_date: editingTaskValues.due_date ?? null,
        responsible_id: editingTaskValues.responsible_id ?? null,
        category_id: editingTaskValues.category_id ?? null,
        project_id: editingTaskValues.project_id ?? null,
      });
      setEditingTaskId(null);
      setEditingTaskValues({});
    } catch {
      // error centralizado en store
    }
  };

  const handleDeleteTask = async (task: TaskBoardTask) => {
    const confirmed = window.confirm('¿Seguro que querés eliminar esta tarea?');
    if (!confirmed) return;
    try {
      await deleteTaskBoardTask(task.id);
    } catch {
      // error centralizado en store
    }
  };

  const handleMoveTask = async (task: TaskBoardTask, newStatus: BoardStatus) => {
    if (task.status === newStatus) return;
    try {
      await updateTaskBoardTask(task.id, { status: newStatus });
    } catch {
      // error centralizado en store
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  };

  const renderBoardChip = (board: TaskBoard) => {
    const isActive = board.id === selectedBoardId;
    return (
      <button
        key={board.id}
        onClick={() => setSelectedBoardId(board.id)}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
          isActive
            ? 'bg-black text-white border-black'
            : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <span className="truncate max-w-[140px] text-left">{board.name}</span>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setEditingBoard(board);
            const statuses =
              board.status_columns && board.status_columns.length > 0
                ? board.status_columns
                : DEFAULT_STATUSES;
            setEditingBoardStatuses(statuses.join(', '));
          }}
          className="text-gray-400 hover:text-gray-100"
        >
          <Edit2 className="w-3 h-3" />
        </button>
      </button>
    );
  };

  const renderTaskCard = (task: TaskBoardTask, index: number) => {
    const responsible = task.responsible_id
      ? taskResponsibles.find(r => r.id === task.responsible_id)
      : undefined;
    const category = task.category_id
      ? taskCategories.find(c => c.id === task.category_id)
      : undefined;
    const project = task.project_id
      ? taskProjects.find(p => p.id === task.project_id)
      : undefined;

    const isEditing = editingTaskId === task.id;

    return (
      <Draggable draggableId={task.id} index={index}>
        {provided => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl p-3.5 mb-3 text-sm shadow-sm hover:shadow-md transition-shadow"
          >
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editingTaskValues.title || ''}
              onChange={e =>
                setEditingTaskValues(prev => ({ ...prev, title: e.target.value }))
              }
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Título de la tarea"
            />
            <div className="flex gap-2">
              <select
                value={editingTaskValues.status || DEFAULT_STATUSES[0]}
                onChange={e =>
                  setEditingTaskValues(prev => ({ ...prev, status: e.target.value }))
                }
                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs"
              >
                {boardStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={editingTaskValues.due_date || ''}
                onChange={e =>
                  setEditingTaskValues(prev => ({ ...prev, due_date: e.target.value }))
                }
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={editingTaskValues.responsible_id || ''}
                onChange={e =>
                  setEditingTaskValues(prev => ({
                    ...prev,
                    responsible_id: e.target.value || null,
                  }))
                }
                className="flex-1 min-w-[120px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs"
              >
                <option value="">Sin responsable</option>
                {taskResponsibles.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <select
                value={editingTaskValues.category_id || ''}
                onChange={e =>
                  setEditingTaskValues(prev => ({
                    ...prev,
                    category_id: e.target.value || null,
                  }))
                }
                className="flex-1 min-w-[120px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs"
              >
                <option value="">Sin categoría</option>
                {taskCategories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={editingTaskValues.project_id || ''}
                onChange={e =>
                  setEditingTaskValues(prev => ({
                    ...prev,
                    project_id: e.target.value || null,
                  }))
                }
                className="flex-1 min-w-[120px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs"
              >
                <option value="">Sin proyecto</option>
                {taskProjects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setEditingTaskId(null);
                  setEditingTaskValues({});
                }}
              >
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSaveTask}>
                Guardar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {task.title}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {category && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[11px]">
                      {category.name}
                    </span>
                  )}
                  {project && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px]">
                      {project.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {task.due_date && (
                  <div className="flex items-center text-[11px] text-gray-500 dark:text-gray-400">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{task.due_date}</span>
                  </div>
                )}
                {responsible && (
                  <div className="flex items-center gap-1">
                    <div className="w-7 h-7 rounded-full bg-gray-900 text-white text-[10px] flex items-center justify-center">
                      {getInitials(responsible.name)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[70%]">
                {task.description || 'Sin descripción'}
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => startEditTask(task)}
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-100"
                  title="Editar"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteTask(task)}
                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
          </div>
        )}
      </Draggable>
    );
  };

  const renderStatusColumn = (status: BoardStatus) => {
    const tasks = groupedTasks[status] || [];
    return (
      <div key={status} className="flex-1 min-w-[220px]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {status}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {tasks.length}
            </span>
          </div>
        </div>
        <Droppable droppableId={status}>
          {provided => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="rounded-xl bg-white/60 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 p-3 min-h-[80px] backdrop-blur-sm"
            >
              {tasks.map((task, index) => renderTaskCard(task, index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const task = boardTasks.find(t => t.id === draggableId);
    if (!task) return;

    const newStatus = destination.droppableId as BoardStatus;

    try {
      await updateTaskBoardTask(task.id, {
        status: newStatus,
        position: destination.index,
      });
    } catch {
      // error centralizado en store
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">
            Gestión
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Tableros
            </h2>
            {selectedBoard && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedBoard.description}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowNewBoardForm(prev => !prev)}
            icon={<Plus className="w-4 h-4" />}
          >
            Nuevo tablero
          </Button>
        </div>
      </div>

      {/* Selector de tableros */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-gray-900/60 px-3 py-2 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Tableros ({taskBoards.length})
          </span>
        </div>
        {taskBoards.length > 0 ? (
          <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
            {taskBoards.map(renderBoardChip)}
          </div>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Todavía no hay tableros. Creá el primero para empezar a organizar tareas.
          </p>
        )}

        {showNewBoardForm && (
          <div className="mt-2 border-t border-gray-200 dark:border-gray-800 pt-2 flex flex-col gap-2">
            <div className="flex flex-col md:flex-row gap-2">
              <input
                type="text"
                placeholder="Nombre del nuevo tablero"
                value={newBoardName}
                onChange={e => setNewBoardName(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white"
              />
              <Button
                size="sm"
                onClick={handleCreateBoard}
                icon={<Plus className="w-3 h-3" />}
              >
                Crear
              </Button>
            </div>
            <textarea
              placeholder="Descripción (opcional)"
              value={newBoardDescription}
              onChange={e => setNewBoardDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white min-h-[40px]"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col xl:flex-row gap-6">
        {/* Main board */}
        <div className="flex-1 space-y-4">
          <Card>
            <CardHeader
              title={selectedBoard ? selectedBoard.name : 'Seleccioná un tablero'}
              action={
                selectedBoard ? (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Arrastrá las tarjetas entre columnas para cambiar de estado.
                  </span>
                ) : null
              }
            />
            <CardContent className="space-y-4">
              {selectedBoard ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      type="text"
                      placeholder="Nueva tarea en este tablero"
                      value={newTaskTitle}
                      onChange={e => setNewTaskTitle(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                    />
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={newTaskStatus}
                        onChange={e => setNewTaskStatus(e.target.value)}
                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs"
                      >
                        {boardStatuses.map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={newTaskDueDate}
                        onChange={e => setNewTaskDueDate(e.target.value)}
                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={newTaskResponsibleId}
                      onChange={e => setNewTaskResponsibleId(e.target.value)}
                      className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs"
                    >
                      <option value="">Sin responsable</option>
                      {taskResponsibles.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={newTaskCategoryId}
                      onChange={e => setNewTaskCategoryId(e.target.value)}
                      className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs"
                    >
                      <option value="">Sin categoría</option>
                      {taskCategories.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={newTaskProjectId}
                      onChange={e => setNewTaskProjectId(e.target.value)}
                      className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs"
                    >
                      <option value="">Sin proyecto</option>
                      {taskProjects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={handleCreateTask}
                      icon={<Plus className="w-4 h-4" />}
                    >
                      Agregar tarea
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {boardStatuses.map(renderStatusColumn)}
                  </div>
                </DragDropContext>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Seleccioná o creá un tablero para empezar a organizar tareas.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {editingBoard && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Editar tablero
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                value={editingBoard.name}
                onChange={e => setEditingBoard({ ...editingBoard, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
              <textarea
                value={editingBoard.description || ''}
                onChange={e =>
                  setEditingBoard({ ...editingBoard, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white min-h-[80px]"
              />
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Estados del tablero (separados por coma)
                </label>
                <input
                  type="text"
                  value={editingBoardStatuses}
                  onChange={e => setEditingBoardStatuses(e.target.value)}
                  placeholder="Ej: Pendiente, En curso, Hecho"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setEditingBoard(null)}
                size="sm"
              >
                Cancelar
              </Button>
              <Button onClick={handleUpdateBoard} size="sm">
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBoardsPage;

